import React, { memo, useState } from "react";
import { signInWithEmailAndPassword } from "firebase/auth";
import { auth } from "../firebase/firebaseConfig";
import { Link, useNavigate } from "react-router-dom";
import { appPrimaryColor, responsiveViewToast } from "../utils/constants";
import { MdEmail, MdBusiness, MdLocationOn } from "react-icons/md";
import { RiLockPasswordLine, RiPhoneLine } from "react-icons/ri";
import { setUser } from "../utils";
import { toast } from "react-toastify";
import { getOrgDetails, registerOrg } from "../api";
import InnerLoader from "../components/microComponents/InnerLoader";

// Separate InputField component with memo
const InputField = memo(({
  label,
  icon: Icon,
  type = "text",
  name,
  value,
  onChange,
  placeholder,
  pattern
}) => (
  <div className="space-y-2">
    <label htmlFor={name} className="block text-sm font-medium text-gray-700">
      {label}
    </label>
    <div className="relative">
      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
        <Icon className="h-5 w-5 text-gray-400" />
      </div>
      <input
        id={name}
        name={name}
        type={type}
        value={value || ''}
        onChange={onChange}
        placeholder={placeholder}
        pattern={pattern}
        className="block w-full pl-10 pr-3 py-2.5 text-gray-900 rounded-lg border border-gray-300 
                         focus:ring-2 focus:ring-darkGreen focus:border-transparent"
      />
    </div>
  </div>
));

const SignUp = () => {
  const navigate = useNavigate();
  const [userDetails, setUserDetails] = useState({
    email: "",
    password: "",
    confirmPassword: "",
    org_name: "",
    phone: "",
    address: "",
    zipcode: "",
    showPassword: false,
    showConfirmPassword: false,
  });
  const [disableButton, setDisableButton] = useState(false);

  const handleInputChange = (field, value, validator = null) => {
    if (validator && !validator(value)) {
      return;
    }
    setUserDetails(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSignUp = async (e) => {
    e.preventDefault();
    setDisableButton(true);
    console.log('user - ', userDetails);

    if (
      userDetails.email === '' ||
      userDetails.password === '' ||
      userDetails.confirmPassword === '' ||
      userDetails.org_name === '' ||
      userDetails.phone === '' ||
      userDetails.address === ''
    ) {
      toast.error("Please fill all fields.", responsiveViewToast(window.innerWidth));
      setDisableButton(false)
      return;
    }

    // check email
    if (!userDetails.email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/u)) {
      toast.error("Please enter a valid email address.", responsiveViewToast(window.innerWidth));
      setDisableButton(false)
      return;
    }

    // Check if password is at least 8 characters long
    if (userDetails.password.length < 8) {
      // setError("Password must be at least 8 characters long.");
      toast.error("Password must be at least 8 characters long.");
      setDisableButton(false)
      return
    }

    // Check if passwords match
    if (userDetails.password !== userDetails.confirmPassword) {
      // setError("Passwords do not match. Please try again.");
      toast.error("Passwords do not match. Please try again.");
      setDisableButton(false)
      return;
    }

    // check phone number is valid
    if (!userDetails.phone.match(/^^$|(\+\d{1,2}\s?)?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/u)) {
      toast.error("Please enter a valid phone number. Example: 1234567890", responsiveViewToast(window.innerWidth));
      setDisableButton(false)
      return;
    }

    // check zipcode is valid
    if (!userDetails.zipcode.match(/^(^$|[0-9]{5}(?:-[0-9]{4})?)$/u)) {
      toast.error("Please enter a valid zipcode.", responsiveViewToast(window.innerWidth));
      setDisableButton(false)
      return;
    }

    // email, phone, org_name, address, password
    const data = {
      email: userDetails.email,
      password: userDetails.password,
      org_name: userDetails.org_name,
      phone: userDetails.phone,
      address: `${userDetails.address}, ${userDetails.zipcode}`,
    }

    registerOrg(data).then((res) => {
      console.log('res - ', res);
      if (res.status === 201) {
        console.log('res - ', res);
        // login in to firebase 
        signInWithEmailAndPassword(auth, userDetails.email, userDetails.password).then((userCredential) => {
          console.log('userCredential - ', userCredential);
          const user = userCredential.user;

          // Get org details
          getOrgDetails(user?.accessToken)
            .then((data) => {
              if (data) {
                console.log("🚀 ~ .then ~ data:", data);
                setUser({ ...user, ...data?.org });
                navigate("/"); // Redirect to the home page
              } else {
                toast.error("Failed to get user details.", responsiveViewToast(window.innerWidth));
                setDisableButton(false)
              }
            })
            .catch((error) => {
              console.error("Error getting user details:", error);
              toast.error("Failed to get user details.", responsiveViewToast(window.innerWidth));
              setDisableButton(false)
            })
          // .finally(() => {
          //   setLoader(false);
          // });

        })

      } else if (res.status === 200) {
        toast.error(res.message, responsiveViewToast(window.innerWidth));
        setDisableButton(false)
      }
    }).catch((error) => {
      console.log('error - ', error);
      setDisableButton(false)
    })

  };

  return (
    <div className="min-h-screen flex flex-col md:flex-row">
      {/* Left Side - Background */}
      <div className="hidden md:flex md:w-1/2 bg-darkGreen items-center justify-center p-12">
        <div className="max-w-md text-white">
          <h1 className="text-4xl font-bold mb-6">Join Share Farm</h1>
          <p className="text-lg opacity-90">
            Create an account to start managing your agricultural data and connect with farmers.
          </p>
        </div>
      </div>

      {/* Right Side - Sign Up Form */}
      <div className="flex-1 flex items-center justify-center p-6 bg-gray-50">
        <div className="w-full max-w-2xl space-y-8 bg-white p-8 rounded-2xl shadow-lg">
          {/* Header */}
          <div className="text-center">
            <h2 className="text-3xl font-bold text-gray-900">Create an Account</h2>
            <p className="mt-2 text-sm text-gray-600">
              Already have an account?{" "}
              <Link to="/login" className="text-darkGreen hover:underline font-medium">
                Sign in
              </Link>
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSignUp} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="md:col-span-2">
                <InputField
                  label="Email Address"
                  icon={MdEmail}
                  type="email"
                  name="email"
                  value={userDetails.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  placeholder="Enter your email"
                />
              </div>

              {/* Password Fields */}
              <div className="md:col-span-2">
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">Password</label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <RiLockPasswordLine className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type={userDetails.showPassword ? "text" : "password"}
                      value={userDetails.password}
                      onChange={(e) => handleInputChange('password', e.target.value)}
                      placeholder="Enter your password"
                      className="block w-full pl-10 pr-12 py-2.5 text-gray-900 rounded-lg border border-gray-300 
                         focus:ring-2 focus:ring-darkGreen focus:border-transparent"
                    />
                    <button
                      type="button"
                      onClick={() => setUserDetails(prev => ({ ...prev, showPassword: !prev.showPassword }))}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    >
                      <span className="text-sm text-gray-500 hover:text-gray-700">
                        {userDetails.showPassword ? 'Hide' : 'Show'}
                      </span>
                    </button>
                  </div>
                </div>
              </div>

              <div className="md:col-span-2">
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">Confirm Password</label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <RiLockPasswordLine className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type={userDetails.showConfirmPassword ? "text" : "password"}
                      value={userDetails.confirmPassword}
                      onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                      placeholder="Confirm your password"
                      className="block w-full pl-10 pr-12 py-2.5 text-gray-900 rounded-lg border border-gray-300 
                         focus:ring-2 focus:ring-darkGreen focus:border-transparent"
                    />
                    <button
                      type="button"
                      onClick={() => setUserDetails(prev => ({ ...prev, showConfirmPassword: !prev.showConfirmPassword }))}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    >
                      <span className="text-sm text-gray-500 hover:text-gray-700">
                        {userDetails.showConfirmPassword ? 'Hide' : 'Show'}
                      </span>
                    </button>
                  </div>
                </div>
              </div>

              <InputField
                label="Organization Name"
                icon={MdBusiness}
                name="org_name"
                value={userDetails.org_name}
                onChange={(e) => handleInputChange('org_name', e.target.value)}
                placeholder="Enter organization name"
              />

              <InputField
                label="Phone Number"
                icon={RiPhoneLine}
                type="tel"
                name="phone"
                value={userDetails.phone}
                onChange={(e) => handleInputChange(
                  'phone',
                  e.target.value,
                  value => !value || (value.length <= 10 && /^\d*$/.test(value))
                )}
                placeholder="Enter phone number"
                pattern="[0-9]{10}"
              />

              <div className="md:col-span-2">
                <InputField
                  label="Address"
                  icon={MdLocationOn}
                  name="address"
                  value={userDetails.address}
                  onChange={(e) => handleInputChange('address', e.target.value)}
                  placeholder="Enter your address"
                />
              </div>

              <InputField
                label="Zipcode"
                icon={MdLocationOn}
                type="number"
                name="zipcode"
                value={userDetails.zipcode}
                onChange={(e) => handleInputChange('zipcode', e.target.value)}
                placeholder="Enter zipcode"
              />
            </div>

            <button
              type="submit"
              disabled={disableButton}
              className={`w-full flex justify-center items-center gap-2 py-3 px-4 rounded-lg
                text-white font-medium transition-all
                ${disableButton ? 'bg-gray-400 cursor-not-allowed' : `${appPrimaryColor} hover:opacity-90`}`}
            >
              {disableButton ? (
                <>
                  <InnerLoader
                    divSize={{ width: "20px", height: "20px" }}
                    loaderSize={{ width: "20px", height: "20px" }}
                  />
                  <span>Creating Account...</span>
                </>
              ) : (
                'Create Account'
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default SignUp;